// Consent capture for onboarding.
//
// Gate 1 (the terms step) captures a single *marketing* decision. WhatsApp
// consent is no longer captured here — it is implied at the candidate details
// step when the optional mobile number is supplied (Figma frame C-2).
//
// ── POLARITY WARNING — READ BEFORE CHANGING ─────────────────────────────────
// The UI checkbox is an OPT-OUT: "I don't want to receive marketing emails".
// The stored `marketing` consent is OPT-IN: `granted = true` means the user
// consented to marketing.
//
// These are OPPOSITE. `buildOptionalConsents` below is the ONE place the
// inversion happens. Do not invert anywhere else, and do not "simplify" this by
// passing the checkbox value straight through to the request body — that would
// silently mail everyone who asked not to be mailed.
//
//   box ticked  (marketingOptOut = true)  ⇒  marketing: false
//   box unticked(marketingOptOut = false) ⇒  marketing: true
//
// Both directions are pinned by ConsentSignup.test.tsx.

/** Which onboarding flow is being rendered. Candidate and employer diverge. */
export type ConsentAudience = 'candidate' | 'employer';

/**
 * Map of `party_consent.consent_type` → granted, sent as `optional_consents`
 * on the onboarding completion endpoints. Keys must exist in the backend
 * allowlist (`allowedOptionalConsents` in
 * services/auth/internal/handler/onboarding_common.go).
 */
export type OptionalConsentState = Record<string, boolean>;

export interface BuildOptionalConsentsInput {
  audience: ConsentAudience;
  /**
   * Raw UI checkbox state. `true` means the user TICKED
   * "I don't want to receive marketing emails" — i.e. they opted OUT.
   */
  marketingOptOut: boolean;
  /**
   * Candidate only: whether an optional mobile number was supplied on the
   * candidate details step. Ignored for employers, who capture no mobile
   * consent at all.
   */
  hasMobile?: boolean;
}

/**
 * Translate the onboarding UI state into the `optional_consents` request body.
 * This is the single inversion point between opt-out UI and opt-in storage.
 */
export function buildOptionalConsents({
  audience,
  marketingOptOut,
  hasMobile = false,
}: BuildOptionalConsentsInput): OptionalConsentState {
  // ⚠️ THE inversion. Ticking the opt-out box withholds marketing consent.
  const consents: OptionalConsentState = { marketing: !marketingOptOut };

  if (audience === 'candidate') {
    // WhatsApp consent is implied by supplying the optional mobile number, and
    // is scoped to job application updates only (frame C-2 copy).
    consents.whatsapp_job_alerts = hasMobile;
    // The C-2 notice explicitly promises the number will NOT be used for
    // marketing, so this consent is never granted during onboarding.
    consents.whatsapp_marketing = false;
  }

  // Employers capture no WhatsApp/mobile consent — the keys are omitted
  // entirely rather than sent as false.
  return consents;
}
